import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../../redux/errors.slice"
import { unknownError } from "../../../../utils/handlers/handle-error"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import * as services from "../../api/project-milestone-dependencies.services"
import type { MilestoneDependencyWriteData } from "../../api/project-milestone-dependencies.services"
import type { MilestoneDependency } from "../../types/project-milestone-dependency.types.ts"
import { projectSliceName } from "../project.constants.ts"

export const fetchProjectMilestoneDependencies = createAsyncThunk<
  MilestoneDependency[],
  string
>(
  `${projectSliceName}/fetchProjectMilestoneDependencies`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getMilestoneDependencies(projectId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data ?? []
  }
)

export const createMilestoneDependency = createAsyncThunk<
  MilestoneDependency,
  MilestoneDependencyWriteData
>(
  `${projectSliceName}/createMilestoneDependency`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createMilestoneDependency(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Dependency added successfully.")

    return (response?.data?.details?.data ??
      response?.data) as MilestoneDependency
  }
)

export const patchMilestoneDependency = createAsyncThunk<
  MilestoneDependency,
  Partial<MilestoneDependencyWriteData> & { id: string }
>(
  `${projectSliceName}/patchMilestoneDependency`,
  async ({ id, ...data }, { dispatch, rejectWithValue }) => {
    const response = await services.patchMilestoneDependency(id, data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Dependency updated successfully.")

    const result = (response?.data?.details?.data ??
      response?.data) as MilestoneDependency
    return { ...result, id }
  }
)

export const deleteMilestoneDependency = createAsyncThunk<string, string>(
  `${projectSliceName}/deleteMilestoneDependency`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteMilestoneDependency(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Dependency deleted successfully.")

    return id
  }
)
